/**
 * Modal para crear/editar Especialidad
 */

import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Input } from "@/components/ui";
import { X, Loader2 } from "lucide-react";
import { useCreateSpecialty, useUpdateSpecialty } from "@/hooks/useCatalogs";
import type { Specialty, ClubType } from "@/lib/services/catalogs";

const specialtySchema = z.object({
  name: z.string().min(3, "El nombre debe tener al menos 3 caracteres"),
  code: z.string().min(2, "El código debe tener al menos 2 caracteres"),
  category: z.string().min(2, "La categoría es requerida"),
  clubTypes: z.array(z.string()).min(1, "Selecciona al menos un tipo de club"),
  description: z.string().optional(),
  level: z.enum(["basico", "avanzado", "master"]).optional(),
});

type SpecialtyForm = z.infer<typeof specialtySchema>;

interface SpecialtyModalProps {
  isOpen: boolean;
  onClose: () => void;
  specialty?: Specialty | null;
}

const clubTypeOptions: { value: ClubType; label: string }[] = [
  { value: "aventureros", label: "Aventureros" },
  { value: "conquistadores", label: "Conquistadores" },
  { value: "guias_mayores", label: "Guías Mayores" },
];

export default function SpecialtyModal({
  isOpen,
  onClose,
  specialty,
}: SpecialtyModalProps) {
  const isEditing = !!specialty;

  const createMutation = useCreateSpecialty();
  const updateMutation = useUpdateSpecialty();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm<SpecialtyForm>({
    resolver: zodResolver(specialtySchema),
    defaultValues: {
      name: specialty?.name || "",
      code: specialty?.code || "",
      category: specialty?.category || "",
      clubTypes: specialty?.clubTypes || [],
      description: specialty?.description || "",
      level: specialty?.level,
    },
  });

  // Reset form when specialty changes
  useEffect(() => {
    if (isOpen) {
      reset({
        name: specialty?.name || "",
        code: specialty?.code || "",
        category: specialty?.category || "",
        clubTypes: specialty?.clubTypes || [],
        description: specialty?.description || "",
        level: specialty?.level,
      });
    }
  }, [specialty, isOpen, reset]);

  const selectedClubTypes = watch("clubTypes");

  const toggleClubType = (type: ClubType) => {
    const current = selectedClubTypes || [];
    if (current.includes(type)) {
      setValue(
        "clubTypes",
        current.filter((t) => t !== type),
      );
    } else {
      setValue("clubTypes", [...current, type]);
    }
  };

  const onSubmit = async (data: SpecialtyForm) => {
    try {
      if (isEditing) {
        await updateMutation.mutateAsync({
          id: specialty.id,
          data: data as Partial<Specialty>,
        });
      } else {
        await createMutation.mutateAsync({
          ...data,
          clubTypes: data.clubTypes as ClubType[],
          status: "active",
        });
      }
      onClose();
    } catch (error) {
      // Error handled by mutation
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            {isEditing ? "Editar Especialidad" : "Nueva Especialidad"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            label="Nombre *"
            {...register("name")}
            error={errors.name?.message}
            placeholder="Ej: Arte Culinario"
          />

          <Input
            label="Código *"
            {...register("code")}
            error={errors.code?.message}
            placeholder="Ej: AC"
          />

          <Input
            label="Categoría *"
            {...register("category")}
            error={errors.category?.message}
            placeholder="Ej: Artes Domésticas"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipos de Club *
            </label>
            <div className="flex gap-2">
              {clubTypeOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggleClubType(option.value)}
                  className={`px-3 py-2 rounded-lg border text-sm transition-colors duration-200 ${
                    selectedClubTypes?.includes(option.value)
                      ? "border-primary-500 bg-primary-50 text-primary-700"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {errors.clubTypes && (
              <p className="text-sm text-red-600 mt-1">
                {errors.clubTypes.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nivel
            </label>
            <select
              {...register("level")}
              className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300"
            >
              <option value="">Sin nivel</option>
              <option value="basico">Básico</option>
              <option value="avanzado">Avanzado</option>
              <option value="master">Master</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descripción
            </label>
            <textarea
              {...register("description")}
              rows={3}
              className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300"
              placeholder="Descripción opcional..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading} className="flex-1">
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isEditing ? (
                "Guardar"
              ) : (
                "Crear"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
